import {
  IBusiness,
  IContact,
  IContactField,
  IContactTag,
  IContactAddress,
} from '@trii/types/dist/Contacts/contacts';
import { ILabel } from '@trii/types/dist/Contacts';
import { ChannelType } from '@trii/types/dist/Common/Channels';
import { requestFormData, requestJson } from './http';
import { UserTrii } from '@trii/types/dist/Users';
import { getApiUrls, joinUrl } from './urls';
import type { UserInfo } from '@trii/types/dist/Users';

type ApiContext = {
  baseUrl: string;
  spaceId: string;
};

type WithSignal = { signal?: AbortSignal };

type ApiArgs = ApiContext & WithSignal;

export type Order = 'ASC' | 'DESC';

export type Filter = {
  column: string;
  condition: string;
  value: string;
};

export type TableItemFormat = 'IContact' | 'IBusiness' | 'IContactInfo';

export type PaginationFolder = 'Main' | 'Trash' | 'MyContacts';

export type Pagination = {
  total: number;
  lastPage: number;
  currentPage: number;
  perPage: number;
  order: Order;
  orderColumn: string;
  filter: Filter[];
  format: TableItemFormat;
  folder?: PaginationFolder;
};

export type PaginationInfo = {
  total: number;
  lastPage: number;
  currentPage: number;
  perPage: number;
  cantPageElements: number;
};

export type BusinessTableData = {
  contactos: IBusiness[];
  contactosIds: unknown[];
  paginacion: PaginationInfo;
};

type FetchContactArgs = ApiArgs & {
  contactId: string;
};

type FetchBusinessArgs = ApiArgs & {
  businessId: string;
};

type FetchLabelsArgs = ApiArgs;

type CreateLabelArgs = ApiArgs & {
  name: string;
};

export async function fetchLabels({ baseUrl, spaceId, signal }: FetchLabelsArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, '/labels');
  return requestJson<ILabel[]>({ url, signal });
}

export async function createLabel({ baseUrl, spaceId, name, signal }: CreateLabelArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, '/labels');
  return requestJson<ILabel>({ url, method: 'POST', body: { name }, signal });
}

export async function fetchContact({ baseUrl, spaceId, contactId, signal }: FetchContactArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, `/contacts/${encodeURIComponent(contactId)}`);
  return requestJson<IContact | IBusiness>({ url, signal });
}

export async function fetchBusiness({ baseUrl, spaceId, businessId, signal }: FetchBusinessArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, `/business/${encodeURIComponent(businessId)}`);
  return requestJson<IContact | IBusiness>({ url, signal });
}

type FetchContactFieldsArgs = ApiArgs & {
  forType: 'contact' | 'business';
};

export async function fetchContactFields({
  baseUrl,
  spaceId,
  forType,
  signal,
}: FetchContactFieldsArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = `${joinUrl(urls.CONTACTS, '/ContactFields')}?filter=custom&for=${encodeURIComponent(forType)}`;
  return requestJson<IContactField[]>({ url, signal });
}

type UpdateEntityArgs = ApiArgs & {
  entityType: 'contact' | 'business';
  entityId: string;
  body: Partial<IContact> | Partial<IBusiness> | { tags: IContactTag[] };
};

export async function updateEntity({
  baseUrl,
  spaceId,
  entityType,
  entityId,
  body,
  signal,
}: UpdateEntityArgs) {
  const path =
    entityType === 'business'
      ? `/business/${encodeURIComponent(entityId)}`
      : `/contacts/${encodeURIComponent(entityId)}`;
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, path);
  return requestJson<IContact | IBusiness>({ url, method: 'PUT', body, signal });
}

type FetchBusinessTableDataArgs = ApiArgs & {
  pagination: Pagination;
};

export async function fetchBusinessTableData({
  baseUrl,
  spaceId,
  pagination,
  signal,
}: FetchBusinessTableDataArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, '/Search');
  return requestJson<BusinessTableData>({ url, method: 'POST', body: pagination, signal });
}

type CreateBusinessArgs = ApiArgs & {
  business: Record<string, unknown>;
};

export async function createBusiness({ baseUrl, spaceId, business, signal }: CreateBusinessArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, '/business');
  return requestJson<IBusiness>({ url, method: 'POST', body: business, signal });
}

type DeleteContactAddressData = {
  contactId: string;
  contactAddressId: string;
  type: ChannelType;
};

type CheckContactAddressData = {
  address: string;
  type: ChannelType;
};

type ResultContactAddressCheck = {
  success: boolean;
  warnings: string[];
};

type DeleteContactAddressArgs = ApiArgs & {
  data: DeleteContactAddressData;
};

type CheckContactAddressArgs = ApiArgs & {
  data: CheckContactAddressData;
};

export type SetContactAddressRequestData = {
  address: string;
  type: ChannelType;
  contactId: string;
};

export type UpdateContactAddressData = {
  contactId: string;
  contactAddressId: string;
  note?: string;
  isFavorite?: boolean;
};

type SetContactAddressArgs = ApiArgs & {
  data: SetContactAddressRequestData;
};

type UpdateContactAddressArgs = ApiArgs & {
  data: UpdateContactAddressData;
};

export async function deleteAddress({ baseUrl, spaceId, data, signal }: DeleteContactAddressArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, '/contactAddresses');
  return requestJson<unknown>({ url, method: 'DELETE', body: data, signal });
}

export async function checkContactAddress({
  baseUrl,
  spaceId,
  data,
  signal,
}: CheckContactAddressArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, '/contactAddresses/check');
  return requestJson<ResultContactAddressCheck>({ url, method: 'POST', body: data, signal });
}

export async function setContactAddress({ baseUrl, spaceId, data, signal }: SetContactAddressArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.CONTACTS, '/contactAddresses/set');
  return requestJson<IContactAddress>({ url, method: 'POST', body: data, signal });
}

export async function updateContactAddress({
  baseUrl,
  spaceId,
  data,
  signal,
}: UpdateContactAddressArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(
    urls.CONTACTS,
    `/contactAddresses/${encodeURIComponent(data.contactAddressId)}`,
  );
  return requestJson<IContactAddress>({ url, method: 'PUT', body: data, signal });
}

type FetchUserTriiArgs = ApiArgs;

export async function fetchUserTrii({ baseUrl, spaceId, signal }: FetchUserTriiArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.SETTINGS, `/user`);
  return requestJson<UserTrii>({ url, signal });
}

type FetchUsersArgs = ApiArgs;

export async function fetchUsers({ baseUrl, spaceId, signal }: FetchUsersArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.SETTINGS, `/users`);
  return requestJson<UserInfo[]>({ url, signal });
}

type FetchUserTriiByIdArgs = ApiArgs & { userId: string };

export async function fetchUserTriiById({
  baseUrl,
  spaceId,
  signal,
  userId,
}: FetchUserTriiByIdArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const url = joinUrl(urls.SETTINGS, `/usersInfo/${userId}`);
  return requestJson<UserTrii>({ url, signal });
}

type UploadAvatarArgs = ApiArgs & {
  contactId: string;
  file: File;
};

export async function uploadAvatar({
  baseUrl,
  spaceId,
  contactId,
  file,
  signal,
}: UploadAvatarArgs) {
  const urls = getApiUrls(baseUrl, spaceId);
  const fd = new FormData();
  fd.append('file', file, file.name);

  const url = joinUrl(
    urls.MEDIA,
    `/upload?module=contacts&folderType=avatars&contactId=${encodeURIComponent(contactId)}`,
  );

  return requestFormData<string>({ url, formData: fd, signal });
}

type FetchContactBoundArgs = WithSignal & { contactId: string };
type FetchBusinessBoundArgs = WithSignal & { businessId: string };
type FetchContactFieldsBoundArgs = WithSignal & { forType: 'contact' | 'business' };
type CreateLabelBoundArgs = WithSignal & { name: string };
type UpdateEntityBoundArgs = WithSignal & {
  entityType: 'contact' | 'business';
  entityId: string;
  body: Partial<IContact> | Partial<IBusiness> | { tags: IContactTag[] };
};
type FetchBusinessTableDataBoundArgs = WithSignal & { pagination: Pagination };
type CreateBusinessBoundArgs = WithSignal & { business: Record<string, unknown> };
type DeleteContactAddressBoundArgs = WithSignal & { data: DeleteContactAddressData };
type CheckContactAddressBoundArgs = WithSignal & { data: CheckContactAddressData };
type FetchUserTriiByIdBoundArgs = WithSignal & { userId: string };
type UploadAvatarBoundArgs = WithSignal & { contactId: string; file: File };
type SetContactAddressBoundArgs = WithSignal & { data: SetContactAddressRequestData };
type UpdateContactAddressBoundArgs = WithSignal & { data: UpdateContactAddressData };

export const createEditContactApi = (baseUrl: string, spaceId: string) => ({
  fetchLabels: ({ signal }: WithSignal = {}) => fetchLabels({ baseUrl, spaceId, signal }),
  createLabel: ({ name, signal }: CreateLabelBoundArgs) =>
    createLabel({ baseUrl, spaceId, name, signal }),
  fetchContact: ({ contactId, signal }: FetchContactBoundArgs) =>
    fetchContact({ baseUrl, spaceId, contactId, signal }),
  fetchBusiness: ({ businessId, signal }: FetchBusinessBoundArgs) =>
    fetchBusiness({ baseUrl, spaceId, businessId, signal }),
  fetchBusinessTableData: ({ pagination, signal }: FetchBusinessTableDataBoundArgs) =>
    fetchBusinessTableData({ baseUrl, spaceId, pagination, signal }),
  createBusiness: ({ business, signal }: CreateBusinessBoundArgs) =>
    createBusiness({ baseUrl, spaceId, business, signal }),
  fetchContactFields: ({ forType, signal }: FetchContactFieldsBoundArgs) =>
    fetchContactFields({ baseUrl, spaceId, forType, signal }),
  updateEntity: ({ entityType, entityId, body, signal }: UpdateEntityBoundArgs) =>
    updateEntity({ baseUrl, spaceId, entityType, entityId, body, signal }),
  deleteAddress: ({ data, signal }: DeleteContactAddressBoundArgs) =>
    deleteAddress({ baseUrl, spaceId, data, signal }),
  deleteAddres: ({ data, signal }: DeleteContactAddressBoundArgs) =>
    deleteAddress({ baseUrl, spaceId, data, signal }),
  checkContactAddress: ({ data, signal }: CheckContactAddressBoundArgs) =>
    checkContactAddress({ baseUrl, spaceId, data, signal }),
  setContactAddress: ({ data, signal }: SetContactAddressBoundArgs) =>
    setContactAddress({ baseUrl, spaceId, data, signal }),
  updateContactAddress: ({ data, signal }: UpdateContactAddressBoundArgs) =>
    updateContactAddress({ baseUrl, spaceId, data, signal }),
  fetchUserTrii: ({ signal }: WithSignal = {}) => fetchUserTrii({ baseUrl, spaceId, signal }),
  fetchUsers: ({ signal }: WithSignal = {}) => fetchUsers({ baseUrl, spaceId, signal }),
  fetchUserTriiById: ({ userId, signal }: FetchUserTriiByIdBoundArgs) =>
    fetchUserTriiById({ baseUrl, spaceId, userId, signal }),
  uploadAvatar: ({ contactId, file, signal }: UploadAvatarBoundArgs) =>
    uploadAvatar({ baseUrl, spaceId, contactId, file, signal }),
});

export type EditContactApi = ReturnType<typeof createEditContactApi>;
